import React, { useState, useEffect, useCallback } from "react";
import Styles from "./CompanyDetails.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminGeneratorRaListing,
  fetchGeneratorCapacities,
  clearActivities,
} from "../../../Redux/Slices/gnaAdminSlice";
import { clearWalletContract } from "../../../Redux/Slices/paymentSlice";
import ListingDetails from "../../Modals/Admin/ListingDetails/ListingDetails";
import ListingTable from "./ListingTable";
import WalletTable from "./WalletTable/WalletTable";

const GeneratorDetails = ({ fullGeneratorDetails }) => {
  const dispatch = useDispatch();
  const generatorId = fullGeneratorDetails?.generatorId;
  const stateLabel = fullGeneratorDetails?.stateLabel;

  const [viewType, setViewType] = useState("details");
  const [listingView, setListingView] = useState({ open: false, data: null });

  const bilateralListings = useSelector(
    (state) => state.gnaAdminSlice.fetchCapacity?.data?.page,
  );
  const raListings = useSelector(
    (state) => state.gnaAdminSlice.fetchRaList?.data?.page,
  );

  useEffect(() => {
    dispatch(clearActivities());
    dispatch(clearWalletContract());
  }, [generatorId, dispatch]);

  useEffect(() => {
    if (!generatorId) return;
    switch (viewType) {
      case "bilateral":
        dispatch(fetchGeneratorCapacities(generatorId));
        break;
      case "ra":
        dispatch(
          fetchAdminGeneratorRaListing({
            id: generatorId,
            type: "RA",
            companyId: fullGeneratorDetails?.companyId,
          }),
        );
        break;
      case "rfp_intrastate":
        dispatch(
          fetchAdminGeneratorRaListing({
            id: generatorId,
            type: "CB",
            companyId: fullGeneratorDetails?.companyId,
            auctionType: "CLOSED",
          }),
        );
        break;
      case "rfp_interstate":
        dispatch(
          fetchAdminGeneratorRaListing({
            id: generatorId,
            type: "CB",
            companyId: fullGeneratorDetails?.companyId,
            auctionType: "OPEN",
          }),
        );
        break;
      default:
        break;
    }
  }, [viewType, generatorId, dispatch]);

  const renderDetails = useCallback(() => {
    const wallet = fullGeneratorDetails?.walletAccount?.displayAmountRemaining;
    const bank = fullGeneratorDetails?.generatorBankDetails;
    const office = fullGeneratorDetails?.generatorOfficeAddress;

    return (
      <div className={Styles.entityDetailSections}>
        {/* Generator Information */}
        <div className={Styles.secCard}>
          <div className={Styles.secCardInner}>
            <div className={Styles.secCardMain}>
              <div className={Styles.secTitle}>Generator Information</div>
              <div className={Styles.secFields}>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Generator ID</span>
                  <span className={Styles.fieldValue}>
                    {fullGeneratorDetails?.generatorId || "—"}
                  </span>
                </div>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Name</span>
                  <span className={Styles.fieldValue}>
                    {fullGeneratorDetails?.generatorName || "—"}
                  </span>
                </div>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Type</span>
                  <span className={Styles.fieldValue}>
                    {fullGeneratorDetails?.specificationName || "—"}
                  </span>
                </div>
              </div>
            </div>
            {/* {wallet && (
              <div className={Styles.walletChip}>
                ₹{" "}
                {Number(wallet).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </div>
            )} */}
          </div>
        </div>

        {/* Bank Details */}
        <div className={Styles.secCard}>
          <div className={Styles.secTitle}>Bank Details</div>
          <div className={Styles.secFields}>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Bank Name</span>
              <span className={Styles.fieldValue}>{bank?.bankName || "—"}</span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Account Number</span>
              <span className={`${Styles.fieldValue} ${Styles.fieldMono}`}>
                {bank?.accountNumber || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>IFSC Code</span>
              <span className={`${Styles.fieldValue} ${Styles.fieldMono}`}>
                {bank?.ifscCode || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Payee Name</span>
              <span
                className={`${Styles.fieldValue} ${!bank?.payee ? Styles.fieldMuted : ""}`}
              >
                {bank?.payee || "Not specified"}
              </span>
            </div>
          </div>
        </div>

        {/* Office Address */}
        <div className={Styles.secCard}>
          <div className={Styles.secTitle}>Office Address</div>
          <div className={Styles.secFields}>
            <div className={`${Styles.field} ${Styles.fieldSpan}`}>
              <span className={Styles.fieldLabel}>Address</span>
              <span className={Styles.fieldValue}>
                {office?.address || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>City</span>
              <span className={Styles.fieldValue}>
                {office?.cityName || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Pincode</span>
              <span className={Styles.fieldValue}>
                {office?.pinCode || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>State</span>
              <span className={Styles.fieldValue}>
                {office?.stateUtName || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Mobile</span>
              <span className={Styles.fieldValue}>
                {office?.mobileNumber || "—"}
              </span>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Email</span>
              <span className={`${Styles.fieldValue} ${Styles.fieldLink}`}>
                {office?.email || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }, [fullGeneratorDetails]);

  const renderActiveView = () => {
    switch (viewType) {
      case "details":
        return renderDetails();
      case "bilateral":
        return (
          <ListingTable
            type="bilateral"
            data={bilateralListings}
            setListingView={setListingView}
          />
        );
      case "ra":
        return (
          <ListingTable
            type="ra"
            entityRole="generator"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "rfp_intrastate":
        return (
          <ListingTable
            type="rfq"
            entityRole="generator"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "rfp_interstate":
        return (
          <ListingTable
            type="rfq"
            entityRole="generator"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "wallet":
        return <WalletTable fullGeneratorDetails={fullGeneratorDetails} />;
      default:
        return null;
    }
  };

  const subTabs = [
    { id: "details", label: "Details" },
    // { id: "bilateral", label: "Bilateral listings" },
    // { id: "ra", label: "Reverse Auctions" },
    // { id: "rfp_interstate", label: "RFP(Interstate)" },
    // {
    //   id: "rfp_intrastate",
    //   label: stateLabel === "OD" ? "TradeConnect" : "RFP(Intrastate)",
    // },
    // { id: "wallet", label: "Transaction details" },
  ];

  return (
    <div className={Styles.entityDetailWrapper}>
      {listingView.open && (
        <ListingDetails
          open={listingView.open}
          handleClose={() => setListingView({ open: false, data: null })}
          data={listingView.data}
        />
      )}
      <div className={Styles.subTabs}>
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            className={`${Styles.subTab} ${viewType === tab.id ? Styles.subTabActive : ""}`}
            onClick={() => setViewType(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={Styles.entityDetailContent}>{renderActiveView()}</div>
    </div>
  );
};

export default GeneratorDetails;
